import React, { FC, useCallback, useEffect, useRef } from 'react';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import {
  useGenericEventsLogger,
  useItemSeenRef,
} from '@wix/data-tools-generic-events';
import { useSettings } from '@wix/tpa-settings/react';
import { UnitType } from '@wix/ambassador-bookings-catalog-search-v1-catalog-search/types';
import { ArrowLeft, ArrowRight } from '@wix/wix-ui-icons-common';
import {
  ImageResizeOptions,
  Text,
  TextButton,
  TextButtonPriority,
  TextTypography as TYPOGRAPHY,
  ThumbnailImage,
} from 'wix-ui-tpa/cssVars';
import { classes, st } from './ServiceCard.st.css';
import { RentalsListDataHooks } from '../dataHooks';
import settingsParams from '../../settingsParams';
import {
  BOOKINGS_SERVICE_ENTITY_TYPE,
  CARD_IMAGE,
  DESCRIPTION_CHAR_LIMIT,
} from '../../../../consts';
import type { RentalServiceViewModel } from '../../../../types/types';

export type ServiceCardProps = {
  service: RentalServiceViewModel;
  onServiceClick: (service: RentalServiceViewModel) => void;
  autoFocus?: boolean;
};

export const ServiceCard: FC<ServiceCardProps> = ({
  service,
  onServiceClick,
  autoFocus = false,
}) => {
  const { t } = useTranslation();
  const { isRTL } = useEnvironment();
  const logger = useGenericEventsLogger();
  const settings = useSettings();

  const display = {
    image: settings.get(settingsParams.showImage),
    badge: settings.get(settingsParams.showBadge),
    title: settings.get(settingsParams.showTitle),
    price: settings.get(settingsParams.showPrice),
    unitPrice: settings.get(settingsParams.showUnitPrice),
    minDuration: settings.get(settingsParams.showMinDuration),
    maxDuration: settings.get(settingsParams.showMaxDuration),
    tagline: settings.get(settingsParams.showTagline),
    bookButton: settings.get(settingsParams.showBookButton),
  };
  const dailyBadgeLabel = settings.get(settingsParams.dailyBadgeLabel);
  const hourlyBadgeLabel = settings.get(settingsParams.hourlyBadgeLabel);
  const bookButtonLabel = settings.get(settingsParams.bookButtonLabel);
  const serviceEntities = { [BOOKINGS_SERVICE_ENTITY_TYPE]: service.id };
  const seenRef = useItemSeenRef({
    componentName: 'service_card',
    entities: serviceEntities,
  });
  const cardRef = useRef<HTMLDivElement | null>(null);

  const setCardRef = useCallback(
    (node: HTMLDivElement | null) => {
      cardRef.current = node;
      seenRef(node);
    },
    [seenRef],
  );

  useEffect(() => {
    if (autoFocus) {
      cardRef.current?.focus();
    }
  }, [autoFocus]);
  const unitBadgeLabel = !display.badge
    ? null
    : service.unit === UnitType.DAY
    ? dailyBadgeLabel
    : service.unit === UnitType.HOUR
    ? hourlyBadgeLabel
    : null;

  const durationParts = service.durationRange
    ? [
        display.minDuration
          ? t('widget.servicesList.duration.min', {
              value: service.durationRange.min,
            })
          : null,
        display.maxDuration
          ? t('widget.servicesList.duration.max', {
              value: service.durationRange.max,
            })
          : null,
      ].filter((part): part is string => part !== null)
    : [];

  const isClickable = !!service.slug;
  // Only clamped when More info can open the rest on the rental page.
  const isLongDescription =
    isClickable && (service.description?.length ?? 0) > DESCRIPTION_CHAR_LIMIT;
  const openRentalPage = () => {
    logger.clickCta({
      componentName: 'rent_now',
      entities: serviceEntities,
    });
    onServiceClick(service);
  };

  const clickableProps = isClickable ? { onClick: openRentalPage } : {};

  return (
    <div
      ref={setCardRef}
      className={classes.root}
      data-hook={RentalsListDataHooks.card}
      tabIndex={-1}
    >
      {display.image ? (
        <div
          className={st(classes.imageWrapper, { isClickable })}
          {...clickableProps}
        >
          {service.imageUrl ? (
            <ThumbnailImage
              className={classes.media}
              data-hook={RentalsListDataHooks.media}
              src={service.imageUrl}
              fluid
              sourceWidth={CARD_IMAGE.width}
              sourceHeight={CARD_IMAGE.height}
              aspectRatio={CARD_IMAGE.width / CARD_IMAGE.height}
              resize={ImageResizeOptions.cover}
              alt={service.imageAltText ?? service.name}
            />
          ) : (
            <div
              className={classes.mediaFallback}
              data-hook={RentalsListDataHooks.mediaFallback}
              aria-hidden="true"
            />
          )}
          {unitBadgeLabel ? (
            <Text
              className={classes.unitBadge}
              data-hook={RentalsListDataHooks.unitBadge}
              typography={TYPOGRAPHY.runningText}
            >
              {unitBadgeLabel}
            </Text>
          ) : null}
        </div>
      ) : null}

      {!display.image && unitBadgeLabel ? (
        <Text
          className={st(classes.unitBadge, { standalone: true })}
          data-hook={RentalsListDataHooks.unitBadge}
          typography={TYPOGRAPHY.runningText}
        >
          {unitBadgeLabel}
        </Text>
      ) : null}

      <div className={classes.content}>
        {display.title ? (
          <Text
            className={st(classes.serviceName, { isClickable })}
            data-hook={RentalsListDataHooks.serviceName}
            typography={TYPOGRAPHY.smallTitle}
            tagName="h2"
            {...clickableProps}
          >
            {service.name}
          </Text>
        ) : null}

        {display.price && (service.isFree || service.price) ? (
          <Text
            className={classes.price}
            data-hook={RentalsListDataHooks.price}
            typography={TYPOGRAPHY.runningText}
          >
            {service.isFree ? (
              t('widget.service.priceFree')
            ) : (
              <>
                {service.price}
                {display.unitPrice && service.unit ? (
                  <span className={classes.priceUnit}>
                    /
                    {service.unit === UnitType.HOUR
                      ? t('widget.service.priceUnit.hour')
                      : t('widget.service.priceUnit.day')}
                  </span>
                ) : null}
              </>
            )}
          </Text>
        ) : null}

        {durationParts.length ? (
          <Text
            className={classes.durationRange}
            data-hook={RentalsListDataHooks.durationRange}
            typography={TYPOGRAPHY.runningText}
          >
            {durationParts.join(' - ')}
          </Text>
        ) : null}

        {display.tagline && service.description ? (
          <Text
            className={st(classes.tagline, { isTruncated: isLongDescription })}
            data-hook={RentalsListDataHooks.tagline}
            typography={TYPOGRAPHY.runningText}
            tagName="p"
          >
            {service.description}
          </Text>
        ) : null}

        {display.tagline && isLongDescription ? (
          <TextButton
            className={classes.moreInfo}
            data-hook={RentalsListDataHooks.taglineMoreInfo}
            priority={TextButtonPriority.secondary}
            onClick={() => onServiceClick(service)}
          >
            {t('widget.servicesList.description.moreInfo')}
          </TextButton>
        ) : null}

        {display.bookButton ? (
          <TextButton
            className={classes.bookButton}
            data-hook={RentalsListDataHooks.bookButton}
            priority={TextButtonPriority.primary}
            aria-label={`${bookButtonLabel} ${service.name}`}
            disabled={!isClickable}
            onClick={openRentalPage}
          >
            {bookButtonLabel}
            {isRTL ? (
              <ArrowLeft
                className={classes.bookButtonIcon}
                aria-hidden="true"
              />
            ) : (
              <ArrowRight
                className={classes.bookButtonIcon}
                aria-hidden="true"
              />
            )}
          </TextButton>
        ) : null}
      </div>
    </div>
  );
};
